import { AuthError } from './../errors/auth.error';
import { DestroyRef, Component, inject, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Params, RouterLink } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { DAMO_AUTH_PATHS } from '../../auth-paths';
import { passwordComplexityValidator, valueMatchValidator } from '../utils/validation.utils';
import { AuthService } from '../services/auth.service';
import { I18N_DEFAULT_LANG, I18N_FRENCH_LANG } from '../../../i18n';

@Component({
  selector: 'app-verify-email',
  standalone: true,
  imports: [ReactiveFormsModule, MatCardModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, TranslatePipe, RouterLink],
  templateUrl: './verify-email.component.html',
  styleUrl: './verify-email.component.scss',
})
export class VerifyEmailComponent implements OnInit {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly fb = inject(FormBuilder);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly form = this.fb.group(
    {
      password: ['', [Validators.required, Validators.minLength(8), passwordComplexityValidator]],
      passwordRepeat: ['', [Validators.required]],
    },
    { validators: [valueMatchValidator('password', 'passwordRepeat')] },
  );

  protected readonly DAMO_AUTH_PATHS = DAMO_AUTH_PATHS;

  protected readonly loading = signal<boolean>(false);
  protected readonly passwordRequired = signal(false);
  protected readonly successMessage = signal<string | null>(null);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly redirectUrl = signal<string | null>(null);
  protected readonly formSuccess = signal<boolean>(false);

  private actionCode: string | null = null;

  ngOnInit(): void {
    this.activatedRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (params: Params) => {
      this.redirectUrl.set(params['redirectUrl'] ?? null);

      const lang = params['lang'] === I18N_FRENCH_LANG ? I18N_FRENCH_LANG : I18N_DEFAULT_LANG;
      this.translate.use(lang);

      this.actionCode = params['oobCode'];

      this.passwordRequired.set(params['passwordSet'] === 'true');
      if (!this.passwordRequired()) {
        this.validateActionCode(this.actionCode || '');
      }
    });
  }

  protected async onSubmit(): Promise<void> {
    if (this.form.invalid || !this.actionCode) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    this.errorMessage.set(null);

    try {
      await this.authService.updateUserPassword(this.actionCode, this.form.value.password);
      await this.authService.applyActionCode(this.actionCode);

      this.formSuccess.set(true);
      this.successMessage.set(this.translate.instant('verifyemail.successreset'));
    } catch (error: unknown) {
      if (error instanceof AuthError) {
        this.errorMessage.set(this.translate.instant(error.message));
      } else {
        this.errorMessage.set(this.translate.instant('verifyemail.unknownerror'));
      }
    } finally {
      this.loading.set(false);
    }
  }

  protected proceedAfterReset(): void {
    const url = this.redirectUrl();
    if (url) {
      globalThis.location.href = url;
    }
  }

  private async validateActionCode(actionCode: string): Promise<void> {
    try {
      await this.authService.applyActionCode(actionCode);
      this.formSuccess.set(true);
      this.successMessage.set(this.translate.instant('verifyemail.success'));
    } catch (error: unknown) {
      if (error instanceof AuthError) {
        this.errorMessage.set(this.translate.instant(error.message));
      } else {
        this.errorMessage.set(this.translate.instant('verifyemail.unknownerror'));
      }
    }
  }
}
